import { useEditor } from '@craftjs/core';
import { useEffect, useState } from 'react';
import type { TRBLNumberCOORDS } from '@builder4/shared/types/layout';
import type { FormProps } from '@builder4/shared/components/Form';
import { StyleField } from '@/components/inspector-fields/StyleField/StyleField';
import { useInspectorNode } from '@/hooks/useInspectorNode';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { FORM_NODE_NAME } from '@/utils/constants';

const NodeFieldStyle = () => {
  const { actions } = useEditor();
  const { id, props: nodeProps, node } = useInspectorNode<Partial<FormProps>>();

  const { getBPProp, setBPProp } = useBreakpointAwareProp(node?.data.displayName || FORM_NODE_NAME);

  // Read props using breakpoint-aware getter
  const fieldBorderRadius = getBPProp(nodeProps, 'fieldBorderRadius');
  const fieldInputPadding = getBPProp(nodeProps, 'fieldInputPadding');
  const fieldInputFontSize = getBPProp(nodeProps, 'fieldInputFontSize');

  // Calculate field height: padding + font size
  const calculateFieldHeight = (): number => {
    const padding = fieldInputPadding || [8, 12, 8, 12];
    const fontSize = parseInt((fieldInputFontSize || '14px').replace('px', ''), 10) || 14;
    return fontSize + padding[0] + padding[2]; // fontSize + top + bottom padding
  };

  // Determine which style preset matches the current radius
  const getActiveStyle = (): 'square' | 'round' | 'pill' | null => {
    if (!fieldBorderRadius) return null;

    const radius = fieldBorderRadius[0];
    const fieldHeight = calculateFieldHeight();

    if (radius === 0) return 'square';
    if (radius >= fieldHeight * 0.5) return 'pill';
    if (radius > 0) return 'round';

    return null;
  };

  const [styleKind, setStyleKind] = useState<'square' | 'round' | 'pill' | null>(getActiveStyle());

  // Update active style when radius, padding, or font size changes
  useEffect(() => {
    setStyleKind(getActiveStyle());
  }, [fieldBorderRadius, fieldInputPadding, fieldInputFontSize]);

  const handleStyleKindChange = (kind: 'square' | 'round' | 'pill') => {
    setStyleKind(kind);

    const fieldHeight = calculateFieldHeight();
    const radiusMap = {
      square: 0,
      round: Math.round(fieldHeight * 0.2),
      pill: Math.round(fieldHeight * 0.5),
    };

    const radius = radiusMap[kind];

    const newRadius: TRBLNumberCOORDS = [radius, radius, radius, radius];

    actions.history.ignore().setProp(id, (props: { fieldBorderRadius?: TRBLNumberCOORDS }) => {
      setBPProp(props, 'fieldBorderRadius', newRadius, FORM_NODE_NAME);
    });
    actions.setProp(id, (props: { fieldBorderRadius?: TRBLNumberCOORDS }) => {
      setBPProp(props, 'fieldBorderRadius', newRadius, FORM_NODE_NAME);
    });
  };

  return <StyleField text="Style" style={styleKind ?? undefined} onClick={handleStyleKindChange} />;
};

export default NodeFieldStyle;
